/**
 * Typed accessors for the `extraSettings` jsonb on Site and Organization.
 *
 * The point of the column is that a new preference costs no migration. The
 * point of this module is that the looseness stops here: every setting is
 * declared once, with a default and a parser, so callers get types and cannot
 * invent a second spelling of the same key.
 *
 * What belongs in there: per-entity preferences nothing queries ACROSS rows.
 * What does not: anything filtered, joined, sorted or billed on — billingPlan,
 * accountType, permissions and friends stay real columns, where constraints
 * and indexes work. The name says "extra" for that reason: the ordinary
 * settings are still columns.
 *
 * Reads are total: a missing, malformed or hand-edited value falls back to the
 * default rather than throwing, because this data is not validated by the DB.
 */

/** Everything storable in Site.extraSettings. Add a key here, not at a call site. */
export interface SiteExtraSettings {
  /**
   * Sidebar title-row style: the default icon+pageName row, or a wide logo
   * image in its place. 'logo' with no logoUrl set falls back to 'icon'.
   */
  titleStyle?: 'icon' | 'logo'
  /** Wide logo image URL, shown when titleStyle is 'logo'. */
  logoUrl?: string

  /**
   * Hide the supporter badge on this site's public sidebar AND drop the site
   * from the /supporters listing. Only meaningful while the owner's
   * billingPlan is a supporter plan.
   */
  supporterBadgeHidden?: boolean

  /**
   * Opt the site out of the public site directory (/api/sites.json), which
   * external services (e.g. Atlas) crawl. Absent = listed; only the opt-out
   * is stored so the default stays "listed" without a backfill.
   */
  directoryOptOut?: boolean
  /** Custom supporter badge text; falls back to DEFAULT_SUPPORTER_MESSAGE. */
  supporterBadgeMessage?: string

  /**
   * <meta name="description"> for the site frontpage, separate from
   * pageDescription (which is markdown shown in the sidebar). Absent = fall
   * back to the plain-text pageDescription. Capped at 300 chars.
   */
  metaDescription?: string

  /**
   * Hide the chapter reading-progress bar at the top of public pages. Absent
   * = shown; only the opt-out is stored (same pattern as directoryOptOut).
   */
  hideReadingProgress?: boolean

  /**
   * Free-form CSS the site manager adds to the site's public pages (theme
   * editor). Stored ALREADY sanitized (src/lib/custom-css.ts) and sanitized
   * again at render; capped at CUSTOM_CSS_MAX_LENGTH.
   */
  customCss?: string

  /**
   * Body font of the public pages, independent of the heading style
   * (typographyPreference). Absent = the heading style's default font
   * (see defaultBodyFont), so sites saved before this key look unchanged.
   */
  bodyFont?: BodyFont
}

/**
 * Heading style, stored in the existing Site.typographyPreference column
 * (no migration): 'modern' = Fancy (the original look, default), 'sober',
 * 'latex'. 'classic' is legacy (= Fancy headings with a Garamond default
 * body font): still rendered, no longer offered in the editor. The CSS lives
 * in globals.css under [data-typography="…"]. See design/themes/proposal.md.
 */
export const THEME_PRESETS = ['modern', 'classic', 'sober', 'latex'] as const
export type ThemePreset = (typeof THEME_PRESETS)[number]

/** Unknown/missing values fall back to 'modern' (the column default). */
export function parseThemePreset(value: unknown): ThemePreset {
  return (THEME_PRESETS as readonly unknown[]).includes(value) ? (value as ThemePreset) : 'modern'
}

/**
 * Body fonts (Site.extraSettings.bodyFont → data-body-font). Each has its own
 * size/weight/line-height in globals.css, tuned to its x-height.
 */
export const BODY_FONTS = ['roboto-slab', 'garamond', 'inter', 'libertinus'] as const
export type BodyFont = (typeof BODY_FONTS)[number]

/** Unknown/missing values → null (= the heading style's default font). */
export function parseBodyFont(value: unknown): BodyFont | null {
  return (BODY_FONTS as readonly unknown[]).includes(value) ? (value as BodyFont) : null
}

/** The font a heading style uses when no body font is chosen. */
export function defaultBodyFont(preset: ThemePreset): BodyFont {
  switch (preset) {
    case 'classic': return 'garamond'
    case 'sober': return 'inter'
    case 'latex': return 'libertinus'
    default: return 'roboto-slab'
  }
}

/** Upper bound for Site.extraSettings.customCss, in characters. */
export const CUSTOM_CSS_MAX_LENGTH = 50_000

/** Shown on the public badge when the supporter has not set a custom message. */
export const DEFAULT_SUPPORTER_MESSAGE = 'I support 🇨🇭open-source'

// Organization.extraSettings has no keys of its own any more (its only one,
// the Brevo newsletterListId, went with Brevo); it is read with the same shape.
type ExtraSettings = SiteExtraSettings

/** Prisma hands back `JsonValue`; narrow it without trusting the contents. */
function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

/**
 * Read the whole bag off an entity. Accepts the entity or the raw column, so a
 * `select: { extraSettings: true }` result can be passed straight in.
 */
export function readExtraSettings(source: { extraSettings?: unknown } | unknown): ExtraSettings {
  const raw = asRecord(source)
  const bag = 'extraSettings' in raw ? asRecord(raw.extraSettings) : raw
  const out: ExtraSettings = {}

  if (bag.titleStyle === 'icon' || bag.titleStyle === 'logo') out.titleStyle = bag.titleStyle
  if (typeof bag.logoUrl === 'string' && bag.logoUrl) out.logoUrl = bag.logoUrl

  if (bag.supporterBadgeHidden === true) out.supporterBadgeHidden = true
  if (bag.directoryOptOut === true) out.directoryOptOut = true
  if (typeof bag.supporterBadgeMessage === 'string' && bag.supporterBadgeMessage.trim()) {
    out.supporterBadgeMessage = bag.supporterBadgeMessage.trim().slice(0, 60)
  }
  if (typeof bag.metaDescription === 'string' && bag.metaDescription.trim()) {
    out.metaDescription = bag.metaDescription.trim().slice(0, 300)
  }
  if (bag.hideReadingProgress === true) out.hideReadingProgress = true
  const bodyFont = parseBodyFont(bag.bodyFont)
  if (bodyFont) out.bodyFont = bodyFont
  // Not re-sanitized here (pure module, no URL allowlist); renderers go
  // through readSiteTheme() in src/lib/site-theme.ts, which does.
  if (typeof bag.customCss === 'string' && bag.customCss.trim()) {
    out.customCss = bag.customCss.slice(0, CUSTOM_CSS_MAX_LENGTH)
  }

  return out
}

/**
 * Merge changes into an existing bag, returning what to write back.
 *
 * Shallow on purpose: these are flat scalars. Passing `undefined` for a key
 * deletes it, which is how a setting is reset to its default.
 */
export function mergeExtraSettings(
  current: unknown,
  changes: Partial<ExtraSettings>
): Record<string, unknown> {
  const raw = asRecord(current)
  const next = { ...asRecord('extraSettings' in raw ? raw.extraSettings : raw) }

  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) delete next[key]
    else next[key] = value
  }

  return next
}
